/**
 * PartnerSettlementHistory Component
 * 협력사 주정산내역 페이지 (본사용/협력사용)
 */
import React, {useCallback, useEffect, useRef, useState} from "react";
import ReactDOM from "react-dom";
import {usePartnerSettlementData} from "../hooks/usePartnerSettlementData";
import {usePartnerSettlementHandlers} from "../hooks/usePartnerSettlementHandlers";
import {useDelayedLoading} from "@/shared/hooks/useDelayedLoading";
import {useUserInfo} from "@/shared/hooks/useCrudOperations";
import {usePagination} from "@/shared/hooks/usePagination";
import {useTableSort} from "@/shared/hooks/useTableSort";
import SortIcon from '@/shared/components/dataDisplay/SortIcon';
import {loadFilters, saveFilters} from "@/shared/utils/filterStorage";
import {shouldShowColumn} from "../../../shared/lib/settlementHelpers";
import { ToastType } from "@/shared/components/feedback/Toast/Toast.types";
import { useToast } from "@/shared/components/feedback/Toast/ToastContext";
import {PartnerSettlement} from "../types/partnerSettlement.types";
import FilterSection from './FilterSection';
import SettlementTableRow from './SettlementTableRow';
import Pagination from '@/shared/components/dataDisplay/Pagination';
import Loading from '@/shared/components/feedback/Loading';
import '@/shared/styles/common-table.css';
import '../styles/PartnerSettlementHistory.css';

/** 페이지당 아이템 수 */
const ITEMS_PER_PAGE = { DEFAULT: 10 };

// ========================================
// 헤더 툴팁 컴포넌트 (Portal 사용)
// ========================================
interface HeaderTooltipProps {
    content: string;
    targetRef: React.RefObject<HTMLElement | null>;
    visible: boolean;
}

const HeaderTooltip: React.FC<HeaderTooltipProps> = ({ content, targetRef, visible }) => {
    const [position, setPosition] = useState({ top: 0, left: 0 });

    useEffect(() => {
        if (visible && targetRef.current) {
            const rect = targetRef.current.getBoundingClientRect();
            setPosition({
                top: rect.bottom + 8,
                left: rect.left + rect.width / 2
            });
        }
    }, [visible, targetRef]);

    if (!visible) return null;

    return ReactDOM.createPortal(
        <div
            className="settlement-header-tooltip"
            style={{
                position: 'fixed',
                top: position.top,
                left: position.left,
                transform: 'translateX(-50%)',
                zIndex: 9999
            }}
        >
            <div className="tooltip-arrow"></div>
            <div className="tooltip-title">계산식</div>
            <div className="tooltip-content">
                {content.split('\n').map((line, idx) => (
                    <p key={idx}>{line}</p>
                ))}
            </div>
        </div>,
        document.body
    );
};

// 테이블 헤더 정의
const TABLE_HEADERS = [
    { key: 'no', label: 'NO', sortable: false, columnIndex: 1, width: '60px', sticky: true, left: '0px' },
    { key: 'supplierName', label: '협력사명', sortable: false, columnIndex: 2, width: '150px', sticky: true, left: '60px' },
    { key: 'businessNumber', label: '사업자번호', sortable: false, columnIndex: 3, width: '120px' },
    { key: 'businessName', label: '사업자명', sortable: false, columnIndex: 4, width: '150px' },
    { key: 'settlementStartDate', label: '정산시작일', sortable: true, columnIndex: 5, width: '120px' },
    { key: 'settlementEndDate', label: '정산종료일', sortable: true, columnIndex: 6, width: '120px' },
    { key: 'paymentDate', label: '지급일', sortable: true, columnIndex: 7, width: '120px', tooltip: '정산종료일 + 2일' },
    { key: 'processCount', label: '처리건수', sortable: true, columnIndex: 8, width: '100px' },
    { key: 'deliveryFee', label: '배달료', sortable: true, columnIndex: 9, width: '120px' },
    { key: 'additionalSettlement', label: '추가정산', sortable: true, columnIndex: 10, width: '120px' },
    { key: 'managementFee', label: '관리비', sortable: true, columnIndex: 11, width: '120px' },
    { key: 'supplyAmount', label: '공급가액', sortable: true, columnIndex: 12, width: '120px', tooltip: '배달료 + 추가정산 + 관리비' },
    { key: 'vat', label: '부가세액', sortable: true, columnIndex: 13, width: '120px', tooltip: '공급가액 × 10%' },
    { key: 'supplyPrice', label: '공급대가', sortable: true, columnIndex: 14, width: '120px', tooltip: '공급가액 + 부가세액' },
    { key: 'branchOperationFee', label: '지점 운영 수수료', sortable: true, columnIndex: 15, width: '140px', tooltip: '부릉라이더스에서 협력사에 부과하는 수수료' },
    { key: 'achievementPayback', label: '달성 페이백', sortable: true, columnIndex: 16, width: '120px', tooltip: '부릉라이더스 → 협력사 페이백 금액\n(예: 건당 80원 수수료 중 30원 페이백)' },
    { key: 'promotion', label: '프로모션', sortable: true, columnIndex: 17, width: '120px', tooltip: '부릉라이더스 → 협력사 프로모션 적용 금액' },
    { key: 'paymentManagementFee', label: '지급관리비(vat포함)', sortable: true, columnIndex: 18, width: '160px', tooltip: '(관리비 + 지점 운영 수수료 + 달성페이백 + 프로모션) × 110%' },
    { key: 'dailyDeduction', label: '일차감', sortable: true, columnIndex: 19, width: '120px', tooltip: '라이더가 협력사에 납부한 공제금액' },
    { key: 'employerEmployment', label: '사업주_고용', sortable: true, columnIndex: 20, width: '120px' },
    { key: 'employerIndustrial', label: '사업주_산재', sortable: true, columnIndex: 21, width: '120px' },
    { key: 'insuranceTotal', label: '보험료합계', sortable: true, columnIndex: 22, width: '120px', tooltip: '사업주_고용 + 사업주_산재' },
    { key: 'actualPayment', label: '실 지급 금액', sortable: true, columnIndex: 23, width: '150px', tooltip: '지급관리비(VAT포함) + 일차감 - 보험료합계' }
] as const;

// ========================================
// 테이블 헤더 컴포넌트
// ========================================
interface TableHeaderProps {
    isSupplierUser: boolean;
    sortConfig: { columnKey: string | null; direction: 'asc' | 'desc' | null };
    handleSort: (key: string) => void;
}

const TableHeader: React.FC<TableHeaderProps> = ({ isSupplierUser, sortConfig, handleSort }) => {
    const [activeTooltip, setActiveTooltip] = useState<string | null>(null);
    const headerRefs = useRef<{ [key: string]: HTMLTableCellElement | null }>({});

    return (
        <thead>
            <tr>
                {TABLE_HEADERS.map(header => {
                    if (!shouldShowColumn(header.columnIndex, isSupplierUser)) return null;

                    const stickyStyle = 'sticky' in header && header.sticky ? {
                        position: 'sticky' as const,
                        left: header.left,
                        zIndex: 2,
                        backgroundColor: 'var(--color-gray-50)'
                    } : {};

                    const isSorted = sortConfig.columnKey === header.key;
                    const direction = isSorted ? sortConfig.direction : null;
                    const hasTooltip = 'tooltip' in header && header.tooltip;

                    return (
                        <th
                            key={header.key}
                            ref={el => { headerRefs.current[header.key] = el; }}
                            style={{
                                width: header.width,
                                minWidth: header.width,
                                ...stickyStyle
                            }}
                            className={`${header.sortable ? 'sortable-header' : ''} ${isSorted ? 'sorted' : ''} ${hasTooltip ? 'has-tooltip' : ''}`}
                            onClick={() => header.sortable && handleSort(header.key)}
                            onMouseEnter={() => hasTooltip && setActiveTooltip(header.key)}
                            onMouseLeave={() => setActiveTooltip(null)}
                        >
                            <span className="header-content">
                                {header.label}
                                {hasTooltip && (
                                    <i className="fas fa-info-circle tooltip-icon"></i>
                                )}
                                {header.sortable && <SortIcon direction={direction} />}
                            </span>
                            {hasTooltip && (
                                <HeaderTooltip
                                    content={header.tooltip}
                                    targetRef={{ current: headerRefs.current[header.key] }}
                                    visible={activeTooltip === header.key}
                                />
                            )}
                        </th>
                    );
                })}
            </tr>
        </thead>
    );
};

const PartnerSettlementHistory: React.FC = () => {
    // ========================================
    // 1. 상수 정의
    // ========================================
    const FILTER_STORAGE_KEY = 'partnerSettlementFilters';
    const INITIAL_LOADING_DELAY = 1500; // 초기 로딩 지연 시간 (1.5초)

    // ========================================
    // 2. Hooks - 사용자 정보 및 권한
    // ========================================
    const { showToast } = useToast();
    const { getUserInfo } = useUserInfo();
    const userInfo = getUserInfo();
    const isSupplierUser = userInfo?.userType === 'SUPPLIER';
    const userSupplierId = isSupplierUser ? userInfo?.userId : null;

    // ========================================
    // 3. Hooks - 초기 데이터 로드 여부 결정
    // ========================================
    // 저장된 필터가 있으면 초기 조회 건너뛰기 (저장된 필터로 조회 예정)
    const skipInitialFetch = React.useMemo(() => {
        const saved = loadFilters<{ acctStDate: string; acctEdDate: string }>(FILTER_STORAGE_KEY);
        return !!(saved?.acctStDate && saved?.acctEdDate);
    }, []);

    // ========================================
    // 4. Hooks - 데이터 관리
    // ========================================
    const {
        settlements,
        suppliers,
        latestStartDate,
        latestEndDate,
        loading,
        refreshSettlements,
        downloadExcel
    } = usePartnerSettlementData(userSupplierId, skipInitialFetch);

    const showLoading = useDelayedLoading(loading);

    // 초기 로딩 상태: 1.5초 타이머 완료 여부와 데이터 로드 완료 여부를 모두 확인
    const [minLoadingTimeElapsed, setMinLoadingTimeElapsed] = useState(false);
    const [initialDataLoaded, setInitialDataLoaded] = useState(false);

    // ========================================
    // 5. State - 로컬 상태 관리
    // ========================================
    // 필터 상태 (sessionStorage에서 복원)
    const [filters, setFilters] = useState(() => {
        const saved = loadFilters<{
            acctStDate: string;
            acctEdDate: string;
            suppId: string;
        }>(FILTER_STORAGE_KEY);

        // 저장된 suppId가 'ALL'이면 빈 문자열로 변환
        const savedSuppId = saved?.suppId === 'ALL' ? '' : (saved?.suppId || '');
        return saved ? {
            acctStDate: saved.acctStDate || '',
            acctEdDate: saved.acctEdDate || '',
            suppId: isSupplierUser ? (userSupplierId || '') : savedSuppId
        } : {
            acctStDate: '',
            acctEdDate: '',
            suppId: isSupplierUser ? (userSupplierId || '') : ''
        };
    });

    // 초기 필터 적용 여부 추적
    const hasAppliedInitialFilter = useRef(false);

    // 정렬 훅 사용
    const {
        sortConfig,
        sortedData: sortedSettlements,
        handleSort
    } = useTableSort<PartnerSettlement>({
        data: settlements || [],
        numericColumns: ['processCount', 'deliveryFee', 'additionalSettlement', 'managementFee', 'supplyAmount', 'vat', 'supplyPrice', 'branchOperationFee', 'achievementPayback', 'promotion', 'paymentManagementFee', 'dailyDeduction', 'employerEmployment', 'employerIndustrial', 'insuranceTotal', 'actualPayment'],
        dateColumns: ['settlementStartDate', 'settlementEndDate', 'paymentDate']
    });

    // ========================================
    // 6. Hooks - 페이지네이션 (정렬된 데이터 기준)
    // ========================================
    const {
        currentPage,
        totalPages,
        currentItems,
        goToPage,
        resetPagination
    } = usePagination({ items: sortedSettlements, itemsPerPage: ITEMS_PER_PAGE.DEFAULT });

    // ========================================
    // 7. Hooks - 핸들러 함수
    // ========================================
    const {
        handleSearch,
        handleExcelDownload
    } = usePartnerSettlementHandlers({
        filters,
        setFilters,
        refreshSettlements,
        downloadExcel,
        resetPagination
    });

    /**
     * 필터 초기화 핸들러
     */
    const handleReset = useCallback(() => {
        // 최근 정산일자로 초기화
        setFilters({
            acctStDate: latestStartDate || '',
            acctEdDate: latestEndDate || '',
            suppId: isSupplierUser ? (userSupplierId || '') : ''
        });
    }, [latestStartDate, latestEndDate, isSupplierUser, userSupplierId]);

    // ========================================
    // 8. Side Effects - 데이터 로드 및 동기화
    // ========================================
    // 페이지 진입 시 최소 1.5초간 로딩 표시
    useEffect(() => {
        const timer = setTimeout(() => {
            setMinLoadingTimeElapsed(true);
        }, INITIAL_LOADING_DELAY);

        return () => clearTimeout(timer);
    }, []);

    // 초기 데이터 로드 완료 감지 (settlements에 데이터가 들어오면 완료)
    useEffect(() => {
        // loading이 false가 되면 (데이터 유무와 관계없이) 로드 완료로 처리
        if (!loading && latestStartDate !== undefined && latestEndDate !== undefined) {
            setInitialDataLoaded(true);
        }
    }, [loading, latestStartDate, latestEndDate]);

    // 필터 변경 시 sessionStorage에 저장
    useEffect(() => {
        saveFilters(FILTER_STORAGE_KEY, filters);
    }, [filters]);

    // 최초 로딩 시 최근 정산일자 설정 및 데이터 조회
    useEffect(() => {
        if (hasAppliedInitialFilter.current) return;

        if (latestStartDate && latestEndDate && !loading) {
            // 저장된 필터가 없으면 최근 정산시작일자와 종료일자로 설정
            if (!filters.acctStDate || !filters.acctEdDate) {
                setFilters(prev => ({ ...prev, acctStDate: latestStartDate, acctEdDate: latestEndDate }));
            }

            // 저장된 필터가 있으면 해당 필터로 조회
            if (skipInitialFetch) {
                const saved = loadFilters<typeof filters>(FILTER_STORAGE_KEY);
                if (saved?.acctStDate && saved?.acctEdDate) {
                    refreshSettlements(
                        saved.acctStDate,
                        saved.acctEdDate,
                        saved.suppId === 'ALL' ? undefined : saved.suppId
                    );
                }
            }

            hasAppliedInitialFilter.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [latestStartDate, latestEndDate, loading, skipInitialFetch]);


    // ========================================
    // 9. Render - 로딩 상태
    // ========================================
    // 초기 로딩: 1.5초 타이머 완료 AND 데이터 로드 완료 시에만 로딩 해제
    const isInitialLoading = !minLoadingTimeElapsed || !initialDataLoaded;
    if (isInitialLoading || showLoading) {
        return <Loading message="협력사 주정산내역을 불러오는 중..." />;
    }

    // ========================================
    // 10. Render - 메인 UI
    // ========================================
    return (
        <div className="supplier-selection-page">
            {/* 페이지 제목 */}
            <div className="page-title-wrapper">
                <h1 className="page-title">협력사 주정산내역</h1>
            </div>

            {/* 필터 섹션 */}
            <FilterSection
                filters={filters}
                suppliers={suppliers}
                isSupplierUser={isSupplierUser}
                onFilterChange={setFilters}
                onSearch={handleSearch}
                onReset={handleReset}
                onExcelDownload={handleExcelDownload}
            />

            {/* 검색 결과 건수 */}
            <div className="result-count">
                <i className="fas fa-list"></i>
                총 <strong>{sortedSettlements.length.toLocaleString()}건</strong>
            </div>

            {/* 테이블 섹션 */}
            <div className="table-container partner-settlement-table-container">
                <table className="supplier-selection-table sortable-table">
                    <TableHeader
                        isSupplierUser={isSupplierUser}
                        sortConfig={sortConfig}
                        handleSort={handleSort}
                    />
                    <tbody>
                        {!loading && sortedSettlements.length === 0 ? (
                            <tr>
                                <td colSpan={23} className="text-center text-gray-600">
                                    조회된 정산내역이 없습니다.
                                </td>
                            </tr>
                        ) : (
                            currentItems.map((settlement, index) => (
                                <SettlementTableRow
                                    key={settlement.no}
                                    settlement={settlement}
                                    index={index}
                                    currentPage={currentPage}
                                    isSupplierUser={isSupplierUser}
                                />
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* 페이지네이션 */}
            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={goToPage}
            />
        </div>
    );
};

export default PartnerSettlementHistory;
